import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { RiSearchLine, RiFilter3Line } from "react-icons/ri";
import axios from "axios";
import "./LabTestManagement.css";
import LabTestModal from "./LabTestModal";

const LabTestManagement = () => {
  const [labTests, setLabTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [selectedLabTest, setSelectedLabTest] = useState(null);

  useEffect(() => {
    fetchLabTests();
  }, []);

  const fetchLabTests = async () => {
    try {
      const response = await axios.get("https://api.swasthmitra.com/api/labtest/all");
      setLabTests(response.data.labTests);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching lab tests:", error);
      setLoading(false);
    }
  };

  const updateStatus = async (id, newStatus) => {
    try {
      await axios.patch(`https://api.swasthmitra.com/api/labtest/${id}/status`, {
        status: newStatus,
      });
      fetchLabTests();
    } catch (error) {
      console.error("Error updating status:", error);
    }
  };

  const filteredLabTests = labTests
    .filter((labTest) => {
      const matchesSearch =
        (labTest.name?.toLowerCase() || "").includes(
          searchTerm.toLowerCase()
        ) || (labTest.phone || "").includes(searchTerm);
      const matchesFilter =
        filterStatus === "all" || labTest.status === filterStatus;
      return matchesSearch && matchesFilter;
    })
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  const getStatusColor = (status) => {
    switch (status) {
      case "pending":
        return "status-pending";
      case "processed":
        return "status-processed";
      case "completed":
        return "status-completed";
      default:
        return "status-default";
    }
  };

  return (
    <div className="lab-test-management">
      <div className="header">
        <h2>Lab Test Management</h2>
        <div className="filters">
          <div className="search-box">
            <RiSearchLine />
            <input
              type="text"
              placeholder="Search by name or phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="status-filter">
            <RiFilter3Line />
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="processed">Processed</option>
              <option value="completed">Completed</option>
            </select>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="loading">Loading lab tests...</div>
      ) : (
        <div className="lab-tests-grid">
          {filteredLabTests.map((labTest) => (
            <motion.div
              key={labTest._id}
              className="lab-test-card"
              whileHover={{ scale: 1.02 }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <div className="card-header">
                <h3>{labTest.name || "No Name"}</h3>
                <span className={`status ${getStatusColor(labTest.status)}`}>
                  {labTest.status || "pending"}
                </span>
              </div>
              <div className="card-body">
                <p>
                  <strong>Phone:</strong> {labTest.phone || "No Phone"}
                </p>
                <p>
                  <strong>Date:</strong>{" "}
                  {new Date(labTest.createdAt).toLocaleDateString()}
                </p>
                <div className="lab-test-image">
                  <img
                    src={labTest.testImage || "default-image-url"}
                    alt="Lab Test"
                    onError={(e) => {
                      e.target.src = "default-image-url"; // Add a default image URL
                      e.target.onerror = null; // Prevent infinite loop
                    }}
                  />
                </div>
              </div>
              <div className="card-footer">
                <select
                  value={labTest.status}
                  onChange={(e) => updateStatus(labTest._id, e.target.value)}
                  className="status-select"
                >
                  <option value="pending">Pending</option>
                  <option value="processed">Processed</option>
                  <option value="completed">Completed</option>
                </select>
                <button
                  className="view-details"
                  onClick={() => setSelectedLabTest(labTest)}
                >
                  View Details
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {selectedLabTest && (
        <AnimatePresence>
          <LabTestModal
            labTest={selectedLabTest}
            onClose={() => setSelectedLabTest(null)}
            onUpdateStatus={updateStatus}
          />
        </AnimatePresence>
      )}
    </div>
  );
};

export default LabTestManagement;
